import { Button, CircularProgress, Dialog } from "@mui/material";
import { Field, FormikProvider, useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { InputField } from "components/InputField";
import { AUTH_USER } from "constants/constants";
import { SLAPSHOT_LOGIN_URL } from "constants/urls";
import {
  getPhoneCountry,
  sendPhoneVerification,
  verifyPhone,
} from "services/PhoneVerificationService";

import styles from "./SecondaryVerification.module.scss";

type Step = "phone" | "code";

const RESEND_COOLDOWN_SECONDS = 30;

// ISO 3166-1 alpha-2 → regional indicator pair, e.g. "IN" → 🇮🇳.
const countryFlag = (country: string | null) => {
  if (!country || !/^[A-Za-z]{2}$/.test(country)) return "";
  return String.fromCodePoint(
    ...Array.from(country.toUpperCase(), (c) => 127397 + c.charCodeAt(0)),
  );
};

interface Props {
  open: boolean;
  onVerified: () => void;
}

const SecondaryVerification: React.FC<Props> = ({ open, onVerified }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberDisplay, setPhoneNumberDisplay] = useState("");
  const [resent, setResent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [dialCode, setDialCode] = useState<string | null>(null);
  const [country, setCountry] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  // Falls back to a full international number when the IP gives us nothing.
  useEffect(() => {
    if (!open) return;
    getPhoneCountry()
      .then((res) => {
        setDialCode(res.dial_code);
        setCountry(res.country);
      })
      .catch(() => {
        setDialCode(null);
        setCountry(null);
      });
  }, [open]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        phoneNumber: dialCode
          ? Yup.string()
              .trim()
              .matches(
                /^\d{4,15}$/,
                "Enter your number without the country code",
              )
              .required("Phone number is required")
          : Yup.string()
              .trim()
              .matches(
                /^\+[1-9]\d{6,18}$/,
                "Start with + and your country code, e.g. +91 98765 43210",
              )
              .required("Phone number is required"),
      }),
    [dialCode],
  );

  const phoneFormik = useFormik({
    initialValues: { phoneNumber: "" },
    validationSchema,
    // Returned so Formik clears `isSubmitting`.
    onSubmit: (values, { setStatus }) => {
      setStatus(undefined);
      const entered = values.phoneNumber.trim();
      return sendPhoneVerification(dialCode ? `${dialCode}${entered}` : entered)
        .then((res) => {
          setPhoneNumber(res.phone_number);
          setPhoneNumberDisplay(res.phone_number_display || res.phone_number);
          setCooldown(RESEND_COOLDOWN_SECONDS);
          setStep("code");
        })
        .catch((err) => {
          setStatus(err?.detail || "Could not send a code. Please try again.");
        });
    },
  });

  const codeFormik = useFormik({
    initialValues: { code: "" },
    validationSchema: Yup.object({
      code: Yup.string().trim().required("Code is required"),
    }),
    onSubmit: (values, { setStatus }) => {
      setStatus(undefined);
      return verifyPhone(phoneNumber, values.code.trim())
        .then((res) => {
          localStorage.setItem(AUTH_USER, JSON.stringify(res.data));
          onVerified();
        })
        .catch((err) => {
          setStatus(err?.detail || "That code didn't work. Please try again.");
        });
    },
  });

  // With a fixed prefix only national digits are typed; drop the trunk "0".
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const value = dialCode
      ? raw.replace(/\D/g, "").replace(/^0+/, "")
      : raw.replace(/\s/g, "");
    phoneFormik.setFieldValue("phoneNumber", value);
  };

  const resend = () => {
    if (cooldown > 0) return;
    setResent(false);
    codeFormik.setStatus(undefined);
    setCooldown(RESEND_COOLDOWN_SECONDS);
    sendPhoneVerification(phoneNumber)
      .then(() => setResent(true))
      .catch((err) => {
        setCooldown(0);
        codeFormik.setStatus(
          err?.detail || "Could not resend the code. Please try again.",
        );
      });
  };

  // The cooldown carries over, so swapping numbers isn't a way around it.
  const editNumber = () => {
    setStep("phone");
    setResent(false);
    codeFormik.resetForm();
    codeFormik.setStatus(undefined);
  };

  const logOut = () => {
    localStorage.clear();
    navigate(SLAPSHOT_LOGIN_URL);
  };

  const sendDisabled = phoneFormik.isSubmitting || cooldown > 0;
  const dialCodePrefix = dialCode
    ? `${countryFlag(country)} ${dialCode}`.trim()
    : undefined;

  return (
    // No `onClose`: logging out is the only way past this gate.
    <Dialog
      open={open}
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            backgroundColor: "transparent",
            backgroundImage: "none",
            boxShadow: "none",
            color: "#ffffff",
          },
        },
      }}
    >
      <div className={styles.dialog}>
        {step === "phone" && (
          <>
            <h3>Verification required</h3>
            <p>
              Your account has been flagged as suspicious. Please enter your
              phone number for verification. Your account will be locked until
              this verification is completed.
            </p>
            <FormikProvider value={phoneFormik}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  phoneFormik.handleSubmit();
                }}
                className={styles.form}
              >
                {phoneFormik.status && (
                  <p className={styles.error}>{phoneFormik.status}</p>
                )}
                <Field
                  name="phoneNumber"
                  component={InputField}
                  label="Phone number"
                  placeholder={dialCode ? "9876543210" : "+91 98765 43210"}
                  autoComplete="tel"
                  onChange={handlePhoneChange}
                  prefix={dialCodePrefix}
                  prefixClassName={styles.dialCode}
                  inputClassName={dialCode ? styles.phoneInput : undefined}
                />
                <div className={styles.actions}>
                  <Button className="noBackgroundButton" onClick={logOut}>
                    Log out
                  </Button>
                  <Button
                    className="slapShotButton"
                    disabled={sendDisabled}
                    onClick={() => phoneFormik.handleSubmit()}
                  >
                    {phoneFormik.isSubmitting && (
                      <CircularProgress
                        size={16}
                        sx={{ color: "white", marginRight: "8px" }}
                      />
                    )}
                    {cooldown > 0 ? `Send code in ${cooldown}s` : "Send code"}
                  </Button>
                </div>
              </form>
            </FormikProvider>
          </>
        )}
        {step === "code" && (
          <>
            <h3>Enter verification code</h3>
            <p>
              A verification code was sent to{" "}
              <strong className={styles.phoneNumber}>
                {phoneNumberDisplay}
              </strong>
              . The code expires in 5 minutes.
            </p>
            <FormikProvider value={codeFormik}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  codeFormik.handleSubmit();
                }}
                className={styles.form}
              >
                {codeFormik.status && (
                  <p className={styles.error}>{codeFormik.status}</p>
                )}
                {resent && !codeFormik.status && (
                  <p className={styles.hint}>A new code has been sent.</p>
                )}
                <Field
                  name="code"
                  component={InputField}
                  label="Verification code"
                  placeholder="123456"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                />
                <div className={styles.secondaryActions}>
                  <Button className="noBackgroundButton" onClick={editNumber}>
                    Use a different number
                  </Button>
                  <Button
                    className="noBackgroundButton"
                    onClick={resend}
                    disabled={cooldown > 0}
                  >
                    {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                  </Button>
                </div>
                <div className={styles.actions}>
                  <Button className="noBackgroundButton" onClick={logOut}>
                    Log out
                  </Button>
                  <Button
                    className="slapShotButton"
                    disabled={codeFormik.isSubmitting}
                    onClick={() => codeFormik.handleSubmit()}
                  >
                    Verify
                  </Button>
                </div>
              </form>
            </FormikProvider>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default SecondaryVerification;
